import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import moment from "moment/moment";
import { select, takeLatest } from "redux-saga/effects";
import { put, takeEvery } from "redux-saga/effects";
import { call } from "typed-redux-saga";

import { callApi } from "../../sagas";
import State from "../../State";
import { Cognito } from "../../utils/cognito";
import errors from "../../utils/errors";
import startDownload from "../../utils/startDownload";
import { setError } from "../App/actions";
import * as actions from "./actions";
import {
  ACTION_DOWNLOAD_PRICING,
  ACTION_LOAD_PRICING,
  ACTION_SAVE_PRICING,
  ACTION_UPLOAD_PRICING,
} from "./constants";
import { Pricing } from "./Pricing";

export interface PricingResponseItem {
  id: string;
  supplierId: string;
  partnerId: string;
  type: string;
  value: string;
  fromDate: Date;
  toDate: Date;
  isDeleted: boolean;
}

interface ValidationResult {
  pricingItems: Array<PricingResponseItem>;
  errors: Array<string>;
}

interface CreatePricingImportResponse {
  id: string;
  uploadUrl: string;
}

interface CreateDownloadFileResponse {
  downloadUrl: string;
}

export function mapToPricing(item: PricingResponseItem): Pricing {
  return {
    id: item.id,
    supplierId: item.supplierId,
    partnerId: item.partnerId,
    type: item.type,
    value: item.value,
    fromDate: moment.utc(item.fromDate),
    toDate: moment.utc(item.toDate),
    isDeleted: item.isDeleted,
  };
}

const getAccessToken = (state: State): string =>
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  state.session.session!.credentials.accessToken;

function* loadPricing(axiosClient: AxiosInstance, cognito: Cognito) {
  try {
    const accessToken: string = yield select(getAccessToken);

    const requestConfig: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    const response: AxiosResponse = yield callApi(
      cognito,
      accessToken,
      axiosClient.get,
      "/admin/agreements/pricing",
      requestConfig
    );

    const pricingItems: Pricing[] = response.data.map(mapToPricing);

    yield put(actions.pricingLoaded(pricingItems));
  } catch (e) {
    const message = errors.toString(e);
    yield put(setError(`unable to load pricing: ${message}`));
  }
}

export function* watchLoadPricing(
  axiosClient: AxiosInstance,
  cognito: Cognito
) {
  yield takeLatest(ACTION_LOAD_PRICING, loadPricing, axiosClient, cognito);
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* uploadPricingSaga(
  axiosClient: AxiosInstance,
  cognito: Cognito,
  action: actions.UploadPricingAction
) {
  try {
    const accessToken: string = yield select(getAccessToken);

    const requestConfig: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    const createImportResponse: AxiosResponse<CreatePricingImportResponse> =
      yield callApi(
        cognito,
        accessToken,
        axiosClient.post,
        "/admin/agreements/pricing/import",
        null,
        requestConfig
      );

    yield call(
      axiosClient.put,
      createImportResponse.data.uploadUrl,
      action.content
    );
    const validateImportResponse: AxiosResponse<ValidationResult> =
      yield callApi(
        cognito,
        accessToken,
        axiosClient.post,
        `/admin/agreements/pricing/import/${createImportResponse.data.id}/validate`,
        null,
        requestConfig
      );
    if (validateImportResponse.data.errors.length === 0) {
      const pricingItems: Pricing[] =
        validateImportResponse.data.pricingItems.map(mapToPricing);

      yield put(
        actions.pricingUploadSucceeded(
          pricingItems,
          createImportResponse.data.id
        )
      );
    } else {
      yield put(
        actions.pricingUploadFailed(validateImportResponse.data.errors)
      );
    }
  } catch (e) {
    const message = errors.toString(e);
    yield put(setError(`unable to upload pricing: ${message}`));
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* watchUploadPricing(
  axiosClient: AxiosInstance,
  cognito: Cognito
) {
  try {
    yield takeLatest(
      ACTION_UPLOAD_PRICING,
      uploadPricingSaga,
      axiosClient,
      cognito
    );
  } catch (error) {
    yield put(setError(error));
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* savePricingSaga(
  axiosClient: AxiosInstance,
  cognito: Cognito,
  action: actions.SavePricingAction
) {
  try {
    const accessToken: string = yield select(getAccessToken);

    yield callApi(
      cognito,
      accessToken,
      axiosClient.post,
      `/admin/agreements/pricing/import/${action.pricingImportId}/save`,
      null,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    yield put(actions.pricingSaved());
  } catch (e) {
    const message = errors.toString(e);
    yield put(setError(`unable to save pricing: ${message}`));
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* watchSavePricing(
  axiosClient: AxiosInstance,
  cognito: Cognito
) {
  try {
    yield takeEvery(ACTION_SAVE_PRICING, savePricingSaga, axiosClient, cognito);
  } catch (error) {
    yield put(setError(error));
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* downloadPricingSaga(axiosClient: AxiosInstance, cognito: Cognito) {
  try {
    const accessToken: string = yield select(getAccessToken);

    const response: AxiosResponse<CreateDownloadFileResponse> = yield callApi(
      cognito,
      accessToken,
      axiosClient.post,
      "/admin/agreements/pricing/export",
      null,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    yield put(actions.downloadPricingStarted());

    yield call(startDownload, response.data.downloadUrl);
  } catch (e) {
    const message = errors.toString(e);
    yield put(setError(`unable to download pricing: ${message}`));
  }
}

export function* watchDownloadPricing(
  axiosClient: AxiosInstance,
  cognito: Cognito
) {
  try {
    yield takeEvery(
      ACTION_DOWNLOAD_PRICING,
      downloadPricingSaga,
      axiosClient,
      cognito
    );
  } catch (error) {
    yield put(setError(error));
  }
}
